import React, { useState, useEffect } from "react";
import axiosClient from "../../axios";
import { useUser } from "../../contexts/UserContext";
import { MathJaxContext, MathJax } from "better-react-mathjax";

export default function Quiz({ termId }) {
  const [question, setQuestion] = useState(null);
  const [index, setIndex] = useState(0);
  const [total, setTotal] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [textAnswer, setTextAnswer] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showOptions, setShowOptions] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showVideo, setShowVideo] = useState(true);
  const [timeLeft, setTimeLeft] = useState(60);
  const [correctCount, setCorrectCount] = useState(0);
  const { user, isLoggedIn } = useUser();

  const [result, setResult] = useState(null);

  useEffect(() => {
    async function fetchQuestion() {
      setTimeLeft(60); // reset timer on question change
      setLoading(true);
      setError("");
      setSelectedOption(null);
      setTextAnswer("");
      setShowOptions(false);
      setSubmitted(false);
      setResult(null);

      try {
        const response = await axiosClient.get("/api/getQuestionsByTermId", {
          params: {
            termId: termId,
            index: index,
          },
        });

        setQuestion(response.data.question);
        setTotal(response.data.total);
      } catch (err) {
        setError(err.response?.data?.message || "Error loading question");
        setQuestion(null);
      } finally {
        setLoading(false);
      }
    }

    fetchQuestion();
  }, [termId, index]);

  useEffect(() => {
    if (submitted) return; // stop timer if already submitted

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          if (!submitted) handleSubmit(); // auto-submit
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [submitted]);

  const handleSubmit = async () => {
    try {
      const response = await axiosClient.post("/api/verify-answer", {
        question_id: question.id,
        selected_option: selectedOption,
        text_answer: textAnswer,
      });

      setResult(response.data);

      const isCorrect = response.data.correct;
      let updatedCorrectCount = correctCount;
      if (isCorrect) {
        updatedCorrectCount += 1;
        setCorrectCount(updatedCorrectCount);
      }
      setSubmitted(true);

      // If this is the last question, save result
      if (index + 1 === total) {
        await handleFinalSave(updatedCorrectCount);
        alert("Quiz complete! Your results have been saved.");
      }
    } catch (err) {
      alert("Failed to submit answer.");
    }
  };

  const handleFinalSave = async (finalCorrectCount) => {
    if (!isLoggedIn) return; // Don't proceed if user is not logged in

    try {
      await axiosClient.post("/api/quiz-results", {
        term_id: termId,
        total_questions: total,
        correct_answers: finalCorrectCount, // +1 if this last one is correct
        user_id: user?.id,
      });
    } catch (err) {
      // You might want to handle the error here
      console.error("Failed to save quiz results:", err);
    }
  };

  const handleNext = () => {
    if (index + 1 < total) {
      setIndex(index + 1);
    }
  };

  if (loading) return <p>Loading question...</p>;
  if (error) return <p className="text-danger">{error}</p>;
  if (!question) return <p>No question available.</p>;

  return (
    <div className="container py-4">
      <div class="row justify-content-center bg-white">
        <div id="col2" class="col-md-8 px-5">
          <MathJaxContext>
            <p>
              <span>{question.qid}. </span>
              <MathJax dynamic inline>
                {question.question_text}
              </MathJax>
            </p>
          </MathJaxContext>

          {/* Text input */}
          <div className="mb-3">
            <textarea
              rows="3"
              className="form-control"
              placeholder="Type your answer here"
              value={textAnswer}
              onChange={(e) => setTextAnswer(e.target.value)}
              disabled={submitted}
            />
          </div>

          {/* Options with Bootstrap Icon toggle */}
          <div className="card options-card">
            <div
              className="card-header d-flex justify-content-between align-items-center"
              style={{ cursor: "pointer" }}
              onClick={() => setShowOptions(!showOptions)}
            >
              <span>View Options</span>
              <i
                className={`bi ${
                  showOptions ? "bi-arrow-up-square" : "bi-arrow-down-square"
                }`}
              ></i>
            </div>
            {showOptions && (
              <div className="card-body">
                <div className="form-group">
                  {[1, 2, 3, 4, 5, 6]
                    .map((i) => ({ id: i, text: question[`option_${i}`] }))
                    .filter((opt) => opt.text)
                    .map((opt) => (
                      <div className="form-check" key={opt.id}>
                        <input
                          type="radio"
                          className="form-check-input"
                          name="aRadio"
                          id={`radio_${opt.id}`}
                          value={opt.id}
                          checked={selectedOption === String(opt.id)}
                          onChange={(e) => setSelectedOption(e.target.value)}
                          disabled={submitted}
                        />
                        <label
                          className="form-check-label"
                          htmlFor={`radio_${opt.id}`}
                        >
                          <MathJaxContext>
                            <MathJax dynamic inline>
                              {opt.text}
                            </MathJax>
                          </MathJaxContext>
                        </label>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>

          {/* Result Feedback */}
          {submitted && result && (
            <div className="mt-4">
              <p>
                {result.correct ? (
                  <span className="text-success">That is right!</span>
                ) : (
                  <span className="text-danger">Sorry, incorrect...</span>
                )}
              </p>
              <p>
                <MathJaxContext>
                  <strong>Answer:</strong>
                  <MathJax dynamic inline>
                    {result.correct_answer}
                  </MathJax>
                </MathJaxContext>
              </p>
              {/* Collapsible Video Info */}
              {result.video_url && (
                <div className="card mt-3">
                  <div
                    className="card-header d-flex justify-content-between align-items-center"
                    style={{ cursor: "pointer" }}
                    onClick={() => setShowVideo((prev) => !prev)}
                  >
                    <span>Explanation:</span>
                    <i
                      className={`bi ${
                        showVideo
                          ? "bi-arrow-up-square"
                          : "bi-arrow-down-square"
                      }`}
                    ></i>
                  </div>
                  {showVideo && (
                    <div className="card-body">
                      <a
                        href={result.video_url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Watch Explanation
                      </a>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Submit or Next */}
          <div className="mt-4 d-flex justify-content-between">
            {!submitted ? (
              <button
                className="btn btn-success"
                onClick={handleSubmit}
                disabled={!textAnswer && !selectedOption}
              >
                Submit
              </button>
            ) : (
              <button
                className="btn btn-primary"
                onClick={handleNext}
                disabled={index + 1 >= total}
              >
                Next
              </button>
            )}
          </div>
        </div>
        <div id="col3" class="col-md-1 px-0">
          <div className="card border-0 rounded-0">
            <div className="card-header text-white bg-info p-0 border-0 rounded-0 text-center">
              <small className="small">Question Number</small>
            </div>
            <div className="card-body border-0 rounded-0 bg-body-secondary">
              <p className="card-text text-center">
                {index + 1}/{total}
              </p>
            </div>
          </div>
          <div className="card mt-3 border-0 rounded-0">
            <div className="card-header text-white bg-warning p-0 border-0 rounded-0 text-center">
              <small className="small">Time remaining</small>
            </div>
            <div className="card-body border-0 rounded-0 bg-body-secondary">
              <p className="card-text text-center">{timeLeft}s</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
